import { FC } from 'react'

import { useFragment } from '@apollo/client'
import {
  AttendanceEditTimesheetRequestApprovableFragmentDoc,
  FetchWorkedTimesDocument,
  TaskAffecteeInfoFragment,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { minutesToText } from 'modules/core/lib/duration'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { EmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'

import CreateShiftRequestContent from './Create'
import DeleteShiftRequestContent from './Delete'
import UpdateShiftRequestContent from './Update'
import { AttendanceEditTimesheetRequestTaskContentTypeProps } from './types'

type AttendanceEditTimesheetRequestTaskContentProps = {
  id: number
  requesterContent: EmployeeTableViewRow
  affectee?: TaskAffecteeInfoFragment
}

type RequestType = 'create_shift' | 'update_shift' | 'delete_shift'

const REQUEST_TYPE_COMPONENTS: Record<
  RequestType,
  FC<AttendanceEditTimesheetRequestTaskContentTypeProps>
> = {
  create_shift: CreateShiftRequestContent,
  update_shift: UpdateShiftRequestContent,
  delete_shift: DeleteShiftRequestContent,
} as const

const isValidRequestType = (requestType: string): requestType is RequestType =>
  requestType === 'create_shift' || requestType === 'update_shift' || requestType === 'delete_shift'

const AttendanceEditTimesheetRequestTaskContent: FC<
  AttendanceEditTimesheetRequestTaskContentProps
> = ({ id, requesterContent, affectee }) => {
  const { navigation } = useNavigation()

  const { data } = useFragment({
    fragment: AttendanceEditTimesheetRequestApprovableFragmentDoc,
    fragmentName: 'AttendanceEditTimesheetRequestApprovable',
    from: {
      __typename: 'AttendanceEditTimesheetRequest',
      id,
    },
    optimistic: false,
  })

  const affecteeName = affectee ? getEmployeeDisplayName(affectee) : undefined

  const referenceDate = data.attendanceShift?.referenceDate ?? data.referenceDate ?? ''

  const date = data.attendanceShift?.date ?? data?.attendanceShiftDate ?? ''

  const { data: workedTimesData } = useQuery(FetchWorkedTimesDocument, {
    variables: {
      employeeIds: [affectee?.id].filter(Boolean),
      startOn: referenceDate || date,
      endOn: referenceDate || date,
    },
    skip: !affectee?.id || (!referenceDate && !date),
  })
  const workedTimes = workedTimesData?.attendance?.workedTimesConnection?.nodes ?? []
  const minutes = workedTimes.reduce((acc, workedTime) => acc + workedTime.minutes, 0)
  const workedTime = minutesToText(minutes)

  if (!data) {
    return null
  }

  if (!data.requestType || !isValidRequestType(data.requestType)) {
    return null
  }

  const RequestComponent = REQUEST_TYPE_COMPONENTS[data.requestType]

  const [year, month, day] = date.split('-')

  const moreDetails = {
    title: i18n.t('tasks.view_more_details'),
    url: navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
      periodType: 'monthly',
      year: Number(year),
      month: Number(month),
      day: Number(day),
      employeeId: affectee?.id ?? 1,
    }),
  }

  return (
    <RequestComponent
      id={id}
      requesterContent={requesterContent}
      moreDetails={moreDetails}
      workedTime={workedTime}
      affecteeName={affecteeName}
    />
  )
}

export default AttendanceEditTimesheetRequestTaskContent

export * from './metadata'
